// Mercapoli - Admin Rider Form (Create/Edit)
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Bike, Loader2, Save } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { admin as adminApi, config as configApi } from '../../lib/api';
import { toast } from 'sonner';

const RiderForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  
  const isEditing = !!id;
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [paises, setPaises] = useState([]);
  
  const [formData, setFormData] = useState({
    email: '',
    nombre: '',
    telefono: '',
    password: '',
    pais: 'GT',
    ciudad: '',
    vehiculo: 'moto',
    licencia: '',
    documento_identidad: '',
    cuenta_banco: '',
    banco: '',
    activo: true
  });

  useEffect(() => {
    loadInitialData();
  }, [id]);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      // Cargar países
      const paisesData = await configApi.getPaises();
      setPaises(paisesData.paises);
      
      // Si es edición, cargar datos del rider
      if (isEditing) {
        const rideres = await adminApi.getRiders();
        const rider = rideres.find(r => r.user_id === id);
        if (rider) {
          setFormData({
            email: rider.email || '',
            nombre: rider.nombre || '',
            telefono: rider.telefono || '',
            password: '', // No se carga la contraseña
            pais: rider.pais || 'GT',
            ciudad: rider.ciudad || '',
            vehiculo: rider.vehiculo || 'moto',
            licencia: rider.licencia || '',
            documento_identidad: rider.documento_identidad || '',
            cuenta_banco: rider.cuenta_banco || '',
            banco: rider.banco || '',
            activo: rider.activo !== false
          });
        }
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al cargar datos');
    }
    setLoading(false);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validaciones
    if (!formData.nombre.trim()) {
      toast.error('El nombre es requerido');
      return;
    }
    if (!formData.email.trim()) {
      toast.error('El email es requerido');
      return;
    }
    if (!formData.telefono.trim()) {
      toast.error('El teléfono es requerido');
      return;
    }
    if (!formData.documento_identidad.trim()) {
      toast.error('El documento de identidad es requerido');
      return;
    }
    if (!isEditing && !formData.password) {
      toast.error('La contraseña es requerida');
      return;
    }
    
    setSaving(true);
    try {
      if (isEditing) {
        const updateData = { ...formData };
        if (!updateData.password) {
          delete updateData.password; // No enviar contraseña vacía
        }
        await adminApi.updateRider(id, updateData);
        toast.success('Rider actualizado');
      } else {
        await adminApi.createRider(formData);
        toast.success('Rider creado');
      }
      
      navigate('/admin/repartidores');
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-brand-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 glass border-b border-border/50">
        <div className="container-app">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate('/admin/repartidores')} className="p-2 -ml-2 rounded-lg hover:bg-muted">
                <ArrowLeft className="h-5 w-5" />
              </button>
              <h1 className="font-heading text-lg font-bold">
                {isEditing ? 'Editar Rider' : 'Nuevo Rider'}
              </h1>
            </div>
            <Button 
              onClick={handleSubmit}
              disabled={saving}
              className="bg-brand-primary hover:bg-brand-secondary"
              data-testid="save-rider-btn"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              Guardar
            </Button>
          </div>
        </div>
      </header>

      <div className="container-app py-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Información Personal */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bike className="h-5 w-5" />
                Información Personal
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="nombre">Nombre Completo *</Label>
                  <Input
                    id="nombre"
                    value={formData.nombre}
                    onChange={(e) => handleChange('nombre', e.target.value)}
                    placeholder="Juan Pérez López"
                    data-testid="rider-name"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="documento_identidad">DPI / Cédula *</Label>
                  <Input
                    id="documento_identidad"
                    value={formData.documento_identidad}
                    onChange={(e) => handleChange('documento_identidad', e.target.value)}
                    placeholder="1234 56789 0101"
                    data-testid="rider-dpi"
                  />
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    placeholder="rider@email.com"
                    disabled={isEditing}
                    data-testid="rider-email"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="telefono">Teléfono *</Label>
                  <Input
                    id="telefono"
                    value={formData.telefono}
                    onChange={(e) => handleChange('telefono', e.target.value)}
                    placeholder="+502 1234 5678"
                    data-testid="rider-phone"
                  />
                </div>
              </div>

              {!isEditing && (
                <div className="space-y-2">
                  <Label htmlFor="password">Contraseña *</Label>
                  <Input
                    id="password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => handleChange('password', e.target.value)}
                    placeholder="Mínimo 6 caracteres"
                    data-testid="rider-password"
                  />
                </div>
              )}

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="pais">País *</Label>
                  <Select value={formData.pais} onValueChange={(v) => handleChange('pais', v)}>
                    <SelectTrigger data-testid="rider-country">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {paises.map(p => (
                        <SelectItem key={p.codigo} value={p.codigo}>
                          {p.nombre}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="ciudad">Ciudad *</Label>
                  <Input
                    id="ciudad"
                    value={formData.ciudad}
                    onChange={(e) => handleChange('ciudad', e.target.value)}
                    placeholder="Ciudad de Guatemala"
                    data-testid="rider-city"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Información de Vehículo */}
          <Card>
            <CardHeader>
              <CardTitle>Información de Vehículo</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="vehiculo">Tipo de Vehículo</Label>
                  <Select value={formData.vehiculo} onValueChange={(v) => handleChange('vehiculo', v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="moto">Motocicleta</SelectItem>
                      <SelectItem value="bicicleta">Bicicleta</SelectItem>
                      <SelectItem value="auto">Automóvil</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="licencia">Número de Licencia</Label>
                  <Input
                    id="licencia"
                    value={formData.licencia}
                    onChange={(e) => handleChange('licencia', e.target.value)}
                    placeholder="A-123456"
                  />
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Switch
                  id="activo"
                  checked={formData.activo}
                  onCheckedChange={(v) => handleChange('activo', v)}
                />
                <Label htmlFor="activo">Rider Activo</Label>
              </div>
            </CardContent>
          </Card>

          {/* Datos Bancarios */}
          <Card>
            <CardHeader>
              <CardTitle>Datos Bancarios (para pagos)</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="banco">Banco</Label>
                  <Input
                    id="banco"
                    value={formData.banco}
                    onChange={(e) => handleChange('banco', e.target.value)}
                    placeholder="Ej: Banrural, Banco Industrial"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="cuenta_banco">Número de Cuenta</Label>
                  <Input
                    id="cuenta_banco"
                    value={formData.cuenta_banco}
                    onChange={(e) => handleChange('cuenta_banco', e.target.value)}
                    placeholder="Número de cuenta bancaria"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Botón guardar móvil */}
          <div className="sm:hidden">
            <Button 
              type="submit"
              disabled={saving}
              className="w-full bg-brand-primary hover:bg-brand-secondary"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              Guardar Rider
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RiderForm;
